import { assertUnreachable, delayPromise, groupByToMultimap, IMultimap, JsonRestApiRequestConfig, restApiService, } from '@tableau/vizportalclient-common-components';
import { ListItem } from '@tableau/widgets';
import { LabelType, localizeLabelTypeName, labelTypeToString } from '../shared/LabelType';
import { Label, LabelContentItem, RestLabelResponse, RestLabelResponseWrapper, RestLabelValueResponse, RestLabelValueResponseWrapper } from './LabelTypes';
import { canSubmitLabel } from './canSubmitLabel';

const ERROR_NOTIFYING_SERVER_RESPONSE_DELAY = 2000;
export const DATA_QUALITY_DIALOG_TYPE_TEST_ID = 'data-quality-dialog-type';
export const SENSITIVITY_LABEL_DIALOG_TYPE_TEST_ID = 'sensitivity-label-dialog-type';
export const CERTIFICATION_LABEL_DIALOG_TYPE_TEST_ID = 'certification-label-dialog-type';

const partialConfig: Pick<JsonRestApiRequestConfig, 'restApiVersion' | 'serverWideRequest' | 'endpoint'> = {
  restApiVersion: '3.16',
  serverWideRequest: false,
  endpoint: 'labels',
};

export type SharedLabelParams = {
  readonly category?: Label['category'];
  readonly contentItems: ReadonlyArray<LabelContentItem>;
  readonly makeJsonRestApiRequest: ReturnType<typeof restApiService>['makeJsonRestApiRequest'];
  readonly siteLuid?: string;
};

export type GetLabelsParams = {
  readonly resolveOwners?: boolean;
} & SharedLabelParams;

export type GetLabelValuesParams = {
  readonly category: { category: Label['category'] };
  readonly makeJsonRestApiRequest: ReturnType<typeof restApiService>['makeJsonRestApiRequest'];
}

export type LabelsKey = {
  readonly contentId: string;
  readonly contentType: string;
};

export async function getLabels<T>(
  params: GetLabelsParams & {
    readonly transform: (response: RestLabelResponse) => T;
  }
): Promise<IMultimap<LabelsKey, T>> {
  const { resolveOwners, transform } = params;

  if (resolveOwners) {
    throw new Error('Resolve owners is not implemented, getLabels should only be used for the actions menu');
  }
  const response = await Get.getLabelsSparse(params);
  const data = response.labelList.label ?? [];
  const labels = groupByToMultimap<RestLabelResponse, LabelsKey, T>({
    data,
    keyTransform: (original) => ({ contentId: original.contentId, contentType: original.contentType.toLowerCase() } as LabelsKey),
    valTransform: transform,
    keyBy: ({ contentId, contentType }) => `${contentType}-${contentId}`,
  });
  return labels;
}

const getConfig: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'POST' };

/**
 * getLabelsSparse is intended to be used for the set warning|certification action and
 * doesn't resolve user information because that is not required for the action
 *
 * @param params The list of assets to be queried for and the optional category to filtery by
 * @returns A collection of unresolved labels, i.e.: there is no owner or asset information
 */
function getLabelsSparse(params: GetLabelsParams): Promise<RestLabelResponseWrapper> {
  const request = contentItemsToContentList(params);
  const queryParams = makeLabelsQueryParams(params);

  const makeJsonRestApiRequest = params.makeJsonRestApiRequest;

  const result = makeJsonRestApiRequest<LabelsContentList, RestLabelResponseWrapper>(getConfig, request, queryParams);
  return result;
}

function getLabelValuesSparse(params: GetLabelValuesParams): Promise<RestLabelValueResponseWrapper> {
  const labelValueConfig = getLabelValueConfig(params.category);
  const makeJsonRestApiRequest = params.makeJsonRestApiRequest;
  const result = makeJsonRestApiRequest<void, RestLabelValueResponseWrapper>(labelValueConfig);

  return result;
}

export async function getLabelValues(params: GetLabelValuesParams): Promise<ReadonlyArray<RestLabelValueResponse>> {
  const response = await getLabelValuesSparse(params);
  const responseList = response.labelValueList.labelValue ?? [];
  return responseList;
}

export function mapLabelValueToListItem(type: RestLabelValueResponse): ListItem {
  switch (type.name) {
    case 'SENSITIVE_DATA':
      return labelTypeListItem(LabelType.SENSITIVE_DATA);
    case 'DEPRECATED':
      return labelTypeListItem(LabelType.DEPRECATED);
    case 'STALE':
      return labelTypeListItem(LabelType.STALE);
    case 'WARNING':
      return labelTypeListItem(LabelType.WARNING);
    case 'MAINTENANCE':
      return labelTypeListItem(LabelType.MAINTENANCE);
    case 'EXTRACT_REFRESH_FAILURE':
      return labelTypeListItem(LabelType.EXTRACT_REFRESH_FAILURE);
    case 'FLOW_RUN_FAILURE':
      return labelTypeListItem(LabelType.FLOW_RUN_FAILURE);
    case 'CERTIFIED':
      return labelTypeListItem(LabelType.CERTIFIED);
    default:
      return {
        content: type.name,
        value: type.name,
        testId: SENSITIVITY_LABEL_DIALOG_TYPE_TEST_ID + '-' + type.name
      }
  }
}

export function mapLabelValuesToDescriptions(labelValues: ReadonlyArray<RestLabelValueResponse>): Map<string, string> {
  return labelValues.reduce((mapping, labelValue) => {
    mapping.set(labelValue.name, labelValue.description);
    return mapping;
  }, new Map<string, string>());
}

function getLabelTestId(type: LabelType): string {
  switch (type) {
    case LabelType.SENSITIVE_DATA:
      return SENSITIVITY_LABEL_DIALOG_TYPE_TEST_ID;
    case LabelType.CERTIFIED:
      return CERTIFICATION_LABEL_DIALOG_TYPE_TEST_ID;
    case LabelType.WARNING:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case LabelType.DEPRECATED:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case LabelType.STALE:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case LabelType.MAINTENANCE:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case LabelType.EXTRACT_REFRESH_FAILURE:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    case LabelType.FLOW_RUN_FAILURE:
      return DATA_QUALITY_DIALOG_TYPE_TEST_ID;
    default:
      assertUnreachable(type);
      return '';
  }
}

function labelTypeListItem(type: LabelType): ListItem {
  return {
    content: localizeLabelTypeName(type),
    value: labelTypeToString(type),
    testId: getLabelTestId(type) + '-' + labelTypeToString(type).toLowerCase()
  };
}


function getLabelValueConfig({ category }: { category: Label['category'] }): JsonRestApiRequestConfig {
  return {
    restApiVersion: 'exp',
    serverWideRequest: false,
    endpoint: `labelValues/?categories=${category}`,
    httpMethod: 'GET',
  };
}

export const Get = {
  getConfig,
  getLabelsSparse,
}

export type UpdateOrDeleteLabelParams = {
  readonly luid?: string;
  readonly labelContent: Pick<Label, 'message' | 'active' | 'elevated' | 'value'>;
} & SharedLabelParams;

export async function setOrDeleteLabel(params: UpdateOrDeleteLabelParams): Promise<RestLabelResponse | Record<string, unknown>> {
  const { luid, labelContent, makeJsonRestApiRequest, category } = params;
  const command = canSubmitLabel({ active: labelContent?.active ?? false, message: labelContent?.message ?? '', category });

  switch (command) {
    case 'DELETE':
      if (luid) {
        return Delete.deleteLabel({ luid, makeJsonRestApiRequest });
      } else {
        return Delete.deleteLabels(params);
      }
    case 'SUBMIT':
      return Put.setLabels(params);
    case 'TOOLONG':
      throw new Error('The label message was too long to submit to the server');
    case 'NOMESSAGE':
      throw new Error('A message is required for a label to be submitted');
    default:
      assertUnreachable(command);
      throw new Error('Unreachable');
  }
}

const putConfig: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'PUT' };

function setLabels(params: UpdateOrDeleteLabelParams): Promise<RestLabelResponse> {
  const queryParams = makeLabelsQueryParams(params);
  const request: SetLabelsBody = {
    ...contentItemsToContentList(params),
    label: {
      ...params.labelContent
    }
  };

  const makeJsonRestApiRequest = params.makeJsonRestApiRequest;

  const result = makeJsonRestApiRequest<SetLabelsBody, RestLabelResponse>(putConfig, request, queryParams)
    .then(async (t) => {
      await delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
      return t;
    });

  return result;
}

type SetLabelsBody = {
  readonly label: UpdateOrDeleteLabelParams['labelContent'];
} & LabelsContentList;

export const Put = {
  putConfig,
  setLabels,
}

async function deleteLabels(params: UpdateOrDeleteLabelParams): Promise<Record<string, unknown>> {
  const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'DELETE', endpoint: `labels` };
  const queryParams = makeLabelsQueryParams(params);
  const request = contentItemsToContentList(params);

  const makeJsonRestApiRequest = params.makeJsonRestApiRequest;

  const result = makeJsonRestApiRequest<LabelsContentList, Record<string, unknown>>(config, request, queryParams);
  await delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);

  return result;
}


async function deleteLabel({ luid, makeJsonRestApiRequest }: DeleteLabelParams): Promise<Record<string, unknown>> {
  const config: JsonRestApiRequestConfig = { ...partialConfig, httpMethod: 'DELETE', endpoint: `labels/${luid}` };

  const result = await makeJsonRestApiRequest<void, Record<string, unknown>>(config, undefined).then(async (t) => {
    await delayPromise(ERROR_NOTIFYING_SERVER_RESPONSE_DELAY);
    return t;
  });

  return result;
}


type DeleteLabelParams = {
  readonly luid: string;
} & Pick<SharedLabelParams, 'makeJsonRestApiRequest'>

export const Delete = {
  deleteLabels,
  deleteLabel
}

type LabelsContentList = {
  readonly contentList: {
    readonly content: ReadonlyArray<{
      readonly contentType: LabelContentItem['contentType'];
      readonly id: string;
    }>;
  };
}

function contentItemsToContentList(params: Pick<UpdateOrDeleteLabelParams, 'contentItems'>): LabelsContentList {
  return {
    contentList: {
      content: params.contentItems.map(i => ({ id: i.contentId, contentType: i.contentType }))
    }
  };
}

function makeLabelsQueryParams(params: { category?: Label['category'] }) {
  const { category } = params;

  if (!category) {
    return undefined;
  }

  return {
    categories: category
  };
}
